import { type FC, useEffect, useCallback } from 'react';
import { useFragment, graphql } from 'react-relay';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import PhotosSearchBrowseModalContainer from 'dibs-interiors-photos/exports/PhotosSearchBrowseModalContainer.tsx';
import { trackPhotoVPVPageView } from '../helpers/photosSearchBrowseTracking.ts';
import { SET_MODAL_OPEN, SET_MODAL_PHOTO } from '../helpers/usePhotoModalReducer.ts';
import PhotoModalCloseButton from 'dibs-interiors-photos/exports/PhotoModalCloseButton.tsx';
import PhotosSearchBrowsePhotoContainer from './PhotosSearchBrowsePhotoContainer.tsx';
import PhotoWrapper from 'dibs-interiors-photos/exports/PhotoWrapper.tsx';
import PhotosModalContentMobile from 'dibs-interiors-photos/exports/PhotosModalContentMobile.tsx';
import PhotoArrows from 'dibs-interiors-photos/exports/PhotoArrows.tsx';
import PhotosSearchBrowsePhotoContainerMobile from './PhotosSearchBrowsePhotoContainerMobile.tsx';
import { usePhotoContainerState } from './usePhotoContainerState.ts';
import { useServerVars } from '../../shared/ServerVarsContext.tsx';
import { useHeadlineContext } from '../PhotosSearchBrowseHeadlineContext.tsx';
import { PAGE_SIZE } from '../constants.ts';

import { type PhotosSearchBrowsePhotoPreviewModal_photo$key } from './__generated__/PhotosSearchBrowsePhotoPreviewModal_photo.graphql.ts';
import { type PhotosSearchBrowsePhotoPreviewModal_photos$key } from './__generated__/PhotosSearchBrowsePhotoPreviewModal_photos.graphql.ts';
import { type UseFacetReducer } from '../helpers/useFacetReducer.ts';
import { type UsePhotoModalReducer } from '../helpers/usePhotoModalReducer.ts';
import { type UseHistoryReducer } from '../helpers/useHistoryReducer.ts';
import { type FacetFilter } from '../helpers/useFacetReducer.ts';

type Props = {
    onClose: () => void;
    isOpen: boolean;
    photo: PhotosSearchBrowsePhotoPreviewModal_photo$key | null | undefined;
    photos: PhotosSearchBrowsePhotoPreviewModal_photos$key | null | undefined;
    modalPhotoIndex: number;
    totalResults: number;
    facetFilter: FacetFilter;
    dispatchFacetFilter: UseFacetReducer['dispatchFacetFilter'];
    dispatchModalPhoto: UsePhotoModalReducer['dispatchModalPhoto'];
    dispatchHistory: UseHistoryReducer['dispatchHistory'];
    userId: string;
};

const PhotosSearchBrowsePhotoPreviewModal: FC<Props> = ({
    onClose: onCloseProp,
    isOpen,
    photo: photoRef,
    photos: photosRef,
    modalPhotoIndex,
    totalResults,
    facetFilter,
    dispatchFacetFilter,
    dispatchModalPhoto,
    dispatchHistory,
    userId,
}) => {
    const photos = useFragment(
        graphql`
            fragment PhotosSearchBrowsePhotoPreviewModal_photos on PhotosSearchBrowseQueryConnection {
                ...PhotosSearchBrowsePhotoContainer_photos
                ...PhotosSearchBrowsePhotoContainerMobile_photos
                totalResults
                edges {
                    node {
                        url
                    }
                }
            }
        `,
        photosRef
    );

    const photo = useFragment(
        graphql`
            fragment PhotosSearchBrowsePhotoPreviewModal_photo on PhotosSearchBrowsePhoto {
                ...PhotosSearchBrowseModalContainer_photo
                designFirm {
                    urlLabel
                }
                project {
                    name
                }
                styles {
                    name
                    urlLabel
                }
            }
        `,
        photoRef
    );

    const designFirm = photo?.designFirm || null;
    const firmUrlLabel = designFirm?.urlLabel;
    const projectName = photo?.project?.name || '';

    const selectedStyleValue = facetFilter.style[0]?.value;
    const preferredStyleTag = selectedStyleValue
        ? (photo?.styles || []).find(style => style?.urlLabel === selectedStyleValue)
        : null;

    const { isDesktop, isMobile } = useServerVars();
    const headline = useHeadlineContext();

    useEffect(() => {
        if (isOpen && firmUrlLabel && projectName) {
            trackPhotoVPVPageView({
                firmUrlLabel,
                projectName,
            });
        }
    }, [isOpen, firmUrlLabel, projectName]);

    const onClose = (): void => {
        dispatchModalPhoto({ type: SET_MODAL_OPEN, isOpen: false });
        dispatchModalPhoto({ type: SET_MODAL_PHOTO, photo: null, photoIndex: 0 });
        onCloseProp();
    };

    const photoEdges = photos?.edges || [];
    const photoUrls = photoEdges.map(edge => edge?.node?.url || null).filter(filterNulls);

    const { currentPhotoIndex, onNext, onPrev } = usePhotoContainerState({
        photoUrls,
        page: facetFilter.page,
        modalPhotoIndex,
        itemsOnPage: facetFilter.first || 0,
        totalResults,
        isOpen,
        dispatchFacetFilter,
        dispatchModalPhoto,
        dispatchHistory,
    });

    const handleSwiperPhotoIndexChange = useCallback(
        ({ direction }: { direction: 'prev' | 'next' }) => {
            if (direction === 'prev') {
                onPrev();
            } else {
                onNext();
            }
        },
        [onNext, onPrev]
    );

    return (
        <PhotosSearchBrowseModalContainer
            onClose={onClose}
            isOpen={isOpen}
            isDesktop={isDesktop}
            isMobile={isMobile}
            photo={photo}
            preferredStyleTag={preferredStyleTag}
            userId={userId}
        >
            {isDesktop ? (
                <PhotoWrapper>
                    <PhotosSearchBrowsePhotoContainer
                        photos={photos}
                        currentPhotoIndex={currentPhotoIndex}
                        modalPhotoIndex={modalPhotoIndex}
                        totalResults={totalResults}
                        userId={userId}
                    />
                    <PhotoModalCloseButton onClick={onClose} />
                    <PhotoArrows
                        onNextClick={onNext}
                        onPrevClick={onPrev}
                        hideNext={currentPhotoIndex === totalResults - 1}
                        hidePrev={currentPhotoIndex === 0}
                    />
                </PhotoWrapper>
            ) : (
                <PhotosModalContentMobile
                    currentIndex={modalPhotoIndex % PAGE_SIZE}
                    onClose={onClose}
                    title={headline}
                    totalItems={PAGE_SIZE}
                    photoContainer={
                        <PhotosSearchBrowsePhotoContainerMobile
                            facetFilter={facetFilter}
                            photos={photos}
                            currentPhotoIndex={currentPhotoIndex}
                            modalPhotoIndex={modalPhotoIndex}
                            totalResults={totalResults}
                            changePhotoIndex={handleSwiperPhotoIndexChange}
                            userId={userId}
                        />
                    }
                />
            )}
        </PhotosSearchBrowseModalContainer>
    );
};

export default PhotosSearchBrowsePhotoPreviewModal;
